/**
 * ServerVariablesPanel — the per-connection raw server view that the Health/Fleet
 * tools don't provide: SHOW GLOBAL VARIABLES (editable) + SHOW GLOBAL STATUS
 * (read-only counters). Pick a connection, search, and edit a dynamic global
 * variable via SET GLOBAL — or, on MySQL 8.0+, SET PERSIST so it survives a
 * restart without touching my.cnf.
 *
 * Phase 2 — "Managed" tab: for servers WITHOUT native SET PERSIST (MariaDB /
 * MySQL < 8.0), a managed rule records a desired global value; a backend
 * reconciler re-applies it via SET GLOBAL after it detects the server restarted.
 * Best-effort + eventual (a gap exists between a restart and the next reconcile),
 * not atomic persistence — the UI says so. Admin-only; every change is audited.
 */
import { createSignal, createMemo, createResource, For, Show } from 'solid-js';
import { api } from '../lib/api.js';
import {
  getServerVariables, getServerStatus, setServerVariable,
  getManagedVariables, setManagedVariable, deleteManagedVariable,
} from '../lib/servervars.js';
import Modal from '../components/Modal.jsx';
import { toast } from '../lib/toast.js';

const fmtTime = (unix) => (unix ? new Date(unix * 1000).toLocaleString() : 'never');

export default function ServerVariablesPanel() {
  const [conns] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; } catch { return []; }
  });
  const [connId, setConnId] = createSignal('');
  const [tab, setTab] = createSignal('variables'); // 'variables' | 'status' | 'managed'
  const [search, setSearch] = createSignal('');

  const key = () => (connId() ? connId() : null);
  const [vars, { refetch: refetchVars }] = createResource(key, getServerVariables);
  const [status, { refetch: refetchStatus }] = createResource(() => (connId() && tab() === 'status' ? connId() : null), getServerStatus);
  const [managed, { refetch: refetchManaged }] = createResource(() => (connId() && tab() === 'managed' ? connId() : null), getManagedVariables);

  const persistSupported = () => !!vars()?.persistSupported;
  const display = () => vars()?.display || status()?.display || '';
  const managedRows = () => managed()?.managed || [];

  const rows = createMemo(() => {
    const src = tab() === 'status' ? (status()?.status || []) : (vars()?.variables || []);
    const q = search().trim().toLowerCase();
    return q ? src.filter((r) => r.name.toLowerCase().includes(q) || String(r.value).toLowerCase().includes(q)) : src;
  });

  // ── edit a global variable ──────────────────────────────────────────────────
  // persist  → SET PERSIST (MySQL 8.0+ only). managed → create a reapply rule
  // (servers without persist). Exactly one is offered, based on persistSupported.
  const [edit, setEdit] = createSignal(null); // { name, value, persist, managed }
  const [saving, setSaving] = createSignal(false);
  const openEdit = (r) => setEdit({ name: r.name, value: String(r.value ?? ''), persist: false, managed: false });
  const saveEdit = async () => {
    const e = edit();
    if (!e) return;
    setSaving(true);
    try {
      if (persistSupported()) {
        await setServerVariable(connId(), { name: e.name, value: e.value, persist: e.persist });
        toast.success(`${e.name} set${e.persist ? ' (persisted)' : ''}`);
      } else if (e.managed) {
        await setManagedVariable(connId(), { name: e.name, value: e.value });
        toast.success(`${e.name} set & managed — will reapply after restart`);
        refetchManaged();
      } else {
        await setServerVariable(connId(), { name: e.name, value: e.value, persist: false });
        toast.success(`${e.name} set (runtime only)`);
      }
      setEdit(null);
      refetchVars();
    } catch (err) {
      toast.error(`Set failed: ${err.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  const unmanage = async (name) => {
    if (!confirm(`Stop managing ${name}? The value already set on the server is kept; it just won't be reapplied after a future restart.`)) return;
    try {
      await deleteManagedVariable(connId(), name);
      toast.success(`${name} no longer managed`);
      refetchManaged();
    } catch (err) {
      toast.error(`Failed: ${err.message || err}`);
    }
  };

  return (
    <div class="sv-panel">
      <div class="sv-toolbar">
        <select class="input sv-sel" value={connId()} onChange={(e) => { setConnId(e.currentTarget.value); setSearch(''); }}>
          <option value="">— connection —</option>
          <For each={conns() || []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
        </select>
        <Show when={connId()}>
          <div class="sv-tabs">
            <button class="sv-tab" classList={{ active: tab() === 'variables' }} onClick={() => setTab('variables')}>System variables</button>
            <button class="sv-tab" classList={{ active: tab() === 'status' }} onClick={() => setTab('status')}>Status</button>
            <button class="sv-tab" classList={{ active: tab() === 'managed' }} onClick={() => setTab('managed')}>Managed</button>
          </div>
          <span class="spacer" />
          <Show when={display()}><span class="muted sv-server">{display()}</span></Show>
          <button class="btn btn-sm btn-ghost" title="Refresh" onClick={() => (tab() === 'status' ? refetchStatus() : tab() === 'managed' ? refetchManaged() : refetchVars())}>↻</button>
          <Show when={tab() !== 'managed'}>
            <input class="input sv-search" placeholder="Filter…" value={search()} onInput={(e) => setSearch(e.currentTarget.value)} />
          </Show>
        </Show>
      </div>

      <Show when={!connId()}>
        <div class="sv-empty">Pick a connection to view its server variables and status.</div>
      </Show>

      {/* ── System variables / Status ─────────────────────────────────────── */}
      <Show when={connId() && tab() !== 'managed'}>
        <Show when={(tab() === 'status' ? status.loading : vars.loading)}>
          <div class="sv-empty">Loading…</div>
        </Show>
        <Show when={tab() === 'variables' && vars.error}><div class="sv-empty sv-err">{String(vars.error?.message || vars.error)}</div></Show>
        <Show when={tab() === 'status' && status.error}><div class="sv-empty sv-err">{String(status.error?.message || status.error)}</div></Show>

        <Show when={!(tab() === 'status' ? status.loading : vars.loading) && !(tab() === 'status' ? status.error : vars.error)}>
          <div class="sv-meta muted">
            {rows().length} {tab() === 'status' ? 'status counters' : 'variables'}
            <Show when={tab() === 'variables'}> · click a row to edit (SET GLOBAL{persistSupported() ? ' / PERSIST' : ''})</Show>
          </div>
          <div class="sv-grid">
            <table>
              <thead><tr><th>Name</th><th>Value</th><Show when={tab() === 'variables'}><th /></Show></tr></thead>
              <tbody>
                <For each={rows()}>
                  {(r) => (
                    <tr classList={{ 'sv-editable': tab() === 'variables' }}
                        onClick={() => tab() === 'variables' && openEdit(r)}>
                      <td class="sv-name">{r.name}</td>
                      <td class="sv-val">{r.value === '' ? <span class="sv-blank">(empty)</span> : r.value}</td>
                      <Show when={tab() === 'variables'}>
                        <td class="sv-edit-col"><span class="sv-edit-hint">✎</span></td>
                      </Show>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </Show>

      {/* ── Managed (keep across restarts) ────────────────────────────────── */}
      <Show when={connId() && tab() === 'managed'}>
        <Show when={managed.loading}><div class="sv-empty">Loading…</div></Show>
        <Show when={managed.error}><div class="sv-empty sv-err">{String(managed.error?.message || managed.error)}</div></Show>
        <Show when={!managed.loading && !managed.error}>
          <div class="sv-meta muted">
            {managedRows().length} managed variable{managedRows().length === 1 ? '' : 's'} · re-applied after a detected restart
            <Show when={persistSupported()}>
              {' '}· this server supports <code class="mono">SET PERSIST</code> — prefer that (edit a variable, tick “Persist”). Managed rules are the fallback for servers without it.
            </Show>
          </div>
          <Show when={managedRows().length === 0}>
            <div class="sv-empty">
              Nothing managed yet. On the <b>System variables</b> tab, click a variable and choose
              “Keep across restarts” to have Ninocat re-apply it via <code class="mono">SET&nbsp;GLOBAL</code> after a server restart.
            </div>
          </Show>
          <Show when={managedRows().length > 0}>
            <div class="sv-grid">
              <table>
                <thead><tr><th>Name</th><th>Desired value</th><th>Last reapplied</th><th>Status</th><th /></tr></thead>
                <tbody>
                  <For each={managedRows()}>
                    {(m) => (
                      <tr>
                        <td class="sv-name">{m.name}</td>
                        <td class="sv-val">{m.value === '' ? <span class="sv-blank">(empty)</span> : m.value}</td>
                        <td class="muted" style="white-space:nowrap;font-size:11.5px;">{fmtTime(m.lastAppliedAt)}</td>
                        <td style="font-size:11.5px;">
                          <Show when={m.lastStatus === 'ok'} fallback={<span style="color:var(--danger);" title={m.lastStatus}>{m.lastStatus ? 'error' : '—'}</span>}>
                            <span style="color:var(--success,#3a9);">ok</span>
                          </Show>
                        </td>
                        <td class="sv-edit-col"><button class="btn btn-sm btn-ghost" title="Stop managing" onClick={() => unmanage(m.name)}>✕</button></td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
          <p class="muted" style="font-size:11px;padding:6px 12px 12px;line-height:1.5;">
            <b>Best-effort, not instant.</b> After a restart the server runs with its configured/default value until
            the next reconcile (~1&nbsp;min), so this isn’t safe for must-be-correct-at-startup settings.
            Only dynamic variables can be managed (read-only / static ones can’t be <code class="mono">SET&nbsp;GLOBAL</code>).
            Ninocat must be running and the connection’s account needs <code class="mono">SUPER</code> /
            <code class="mono">SYSTEM_VARIABLES_ADMIN</code>. Every reapply is written to the audit log.
          </p>
        </Show>
      </Show>

      {/* Edit a global variable */}
      <Modal open={!!edit()} onClose={() => setEdit(null)} title={`Set ${edit()?.name || ''}`}>
        <Show when={edit()}>
          <div class="settings-row" style="align-items:flex-start;">
            <label>Value</label>
            <input class="input mono" style="min-width:260px;" value={edit().value} autofocus
                   onInput={(e) => setEdit({ ...edit(), value: e.currentTarget.value })}
                   onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); }} />
          </div>

          {/* MySQL 8.0+: native SET PERSIST */}
          <Show when={persistSupported()}>
            <label class="checkbox-label" style="margin-top:8px;" title="Write with SET PERSIST so it survives a server restart (mysqld-auto.cnf — no my.cnf edit).">
              <input type="checkbox" checked={edit().persist}
                     onChange={(e) => setEdit({ ...edit(), persist: e.currentTarget.checked })} />
              <span>Persist across restart (SET PERSIST)</span>
            </label>
          </Show>

          {/* MariaDB / MySQL < 8.0: managed reapply rule */}
          <Show when={!persistSupported()}>
            <label class="checkbox-label" style="margin-top:8px;" title="This server has no SET PERSIST. Ninocat will re-apply this value via SET GLOBAL after it detects a restart (best-effort, eventual).">
              <input type="checkbox" checked={edit().managed}
                     onChange={(e) => setEdit({ ...edit(), managed: e.currentTarget.checked })} />
              <span>Keep across restarts (managed reapply)</span>
            </label>
            <Show when={edit().managed}>
              <p class="muted" style="font-size:11px;margin:6px 0 0;line-height:1.5;">
                This server has no built-in persist (MariaDB / MySQL&nbsp;&lt;&nbsp;8.0). Ninocat re-applies the value after a
                detected restart — <b>best-effort and eventual</b> (a gap exists between the restart and the next reconcile),
                not atomic persistence. Manage on the <b>Managed</b> tab.
              </p>
            </Show>
          </Show>

          <p class="muted" style="font-size:11px;margin:8px 0 0;">
            Runs <code class="mono">SET {persistSupported() && edit().persist ? 'PERSIST' : 'GLOBAL'} {edit().name} = …</code> — needs SUPER / SYSTEM_VARIABLES_ADMIN.
            Read-only / static variables can't be set at runtime.
          </p>
          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px;">
            <button class="btn btn-ghost" onClick={() => setEdit(null)}>Cancel</button>
            <button class="btn btn-primary" disabled={saving()} onClick={saveEdit}>{saving() ? 'Setting…' : (!persistSupported() && edit().managed ? 'Set & manage' : 'Set')}</button>
          </div>
        </Show>
      </Modal>
    </div>
  );
}
